import dayjs from 'dayjs';
import {
  addDemoAppointment,
  getAppointmentPatches,
  getExtraAppointments,
  getExtraClients,
  getExtraExpenses,
  isAppointmentDeleted,
  isExpenseDeleted,
  isQueueRemoved,
  isReviewReported,
  nextDemoId,
} from './session';

const FIRST_NAMES = [
  'Giulia', 'Marco', 'Sara', 'Luca', 'Chiara', 'Davide', 'Elena', 'Andrea',
  'Francesca', 'Matteo', 'Valentina', 'Simone', 'Alessia', 'Federico', 'Martina',
  'Lorenzo', 'Greta', 'Nicola', 'Ilaria', 'Stefano', 'Beatrice', 'Alessandro',
  'Sofia', 'Giovanni',
];
const LAST_NAMES = [
  'Rossi', 'Bianchi', 'Ferrari', 'Romano', 'Esposito', 'Colombo', 'Ricci',
  'Marino', 'Greco', 'Bruno', 'Gallo', 'Conti', 'De Luca', 'Mancini', 'Costa',
  'Giordano', 'Rizzo', 'Lombardi', 'Moretti', 'Barbieri', 'Fontana', 'Santoro',
  'Mariani', 'Rinaldi',
];
const REVIEW_TEXTS = [
  'Taglio perfetto, ambiente super accogliente. Tornerò senz’altro.',
  'Puntuali e professionali, consiglio a tutti.',
  'Ottimo servizio, la barba è venuta esattamente come la volevo.',
  'Locale pulito e staff gentile, esperienza top.',
  'Prenotazione facile dall’app, zero attesa.',
  'Molto bravi, mi hanno consigliato il taglio giusto per me.',
  'Ci vengo da mesi, sempre un piacere.',
  'Rapporto qualità-prezzo ottimo.',
  'Atmosfera rilassata, musica giusta, risultato impeccabile.',
  'Prima volta e già fissato il prossimo appuntamento.',
  'Taglio moderno e preciso, bravissimi.',
  'Mi hanno fatto sentire a casa, super consigliato.',
];

const mulberry32 = (seed) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const hashSeed = (value) => {
  const text = String(value || 'demo');
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const padTime = (hours, minutes) =>
  `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;

const addMinutes = (hours, minutes, delta) => {
  const total = hours * 60 + minutes + delta;
  const nextH = Math.floor(total / 60);
  const nextM = total % 60;
  return { hours: nextH, minutes: nextM, label: padTime(nextH, nextM) };
};

export const serviceDuration = (service) => {
  const raw = service?.durata ?? service?.time ?? service?.duration ?? 30;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 30;
};

export const serviceId = (service) => service?.idservezio ?? service?.idServizio ?? service?.id;

export const workerId = (worker) => worker?.idpoltrona ?? worker?.idPoltrona ?? worker?.id;

export const workerName = (worker) =>
  worker?.nome_referente || worker?.nome || worker?.name || 'Poltrona';

const activeWorkers = (workers) =>
  (Array.isArray(workers) ? workers : []).filter(
    (worker) => !worker?.disabilitato && Number(worker?.eliminato) !== 1,
  );

const activeServices = (services) =>
  (Array.isArray(services) ? services : []).filter(
    (service) => !service?.disabled && !service?.disabilitato,
  );

const mapService = (service) => ({
  idservezio: serviceId(service),
  idServizio: serviceId(service),
  nome: service?.nome || service?.name || 'Servizio',
  nomeServizio: service?.nome || service?.name || 'Servizio',
  durata: serviceDuration(service),
  time: serviceDuration(service),
  prezzo: Number(service?.prezzo) || 25,
  color: service?.color || '#858AF9',
});

const phoneFor = (index) => `333${String(1000000 + index * 17).slice(0, 7)}`;

const cache = new Map();

const buildBase = ({ salonId, workers, services, today }) => {
  const rng = mulberry32(hashSeed(`${salonId}|${today}`));
  const chairs = activeWorkers(workers);
  const catalog = activeServices(services).map(mapService);
  const fallbackService = catalog[0] || {
    idservezio: -1,
    idServizio: -1,
    nome: 'Taglio',
    nomeServizio: 'Taglio',
    durata: 30,
    time: 30,
    prezzo: 25,
    color: '#858AF9',
  };

  const clients = FIRST_NAMES.map((nome, index) => {
    const cognome = LAST_NAMES[index % LAST_NAMES.length];
    const idutente = -1001 - index;
    const totPren = Math.max(2, Math.round(48 - index * 3 + rng() * 6));
    const daysAgo = 1 + Math.floor(rng() * (index < 10 ? 12 : 80));
    const lastPren = dayjs(today).subtract(daysAgo, 'day').format('YYYY-MM-DD');
    const disabilitato = index === 22 ? 1 : 0;
    const createFromSalone = index % 5 === 0 ? 1 : 0;
    const recentlyActive = daysAgo <= 30 && !disabilitato;
    return {
      idutente,
      nome,
      cognome,
      cellulare: phoneFor(index),
      email: `${nome.toLowerCase()}.${cognome.toLowerCase().replace(/\s/g, '')}@demo.it`,
      tipo: index < 3 ? 3 : 0,
      totPren,
      lastPren,
      createFromSalone,
      cellulare_verificato: 1,
      disabilitato,
      ultimo_accesso: recentlyActive
        ? dayjs(today).subtract(Math.min(daysAgo, 20), 'day').toISOString()
        : dayjs(today).subtract(40 + index, 'day').toISOString(),
      data_iscrizione: dayjs(today).subtract(90 + index * 8, 'day').toISOString(),
      img: null,
    };
  });

  const bestUser = [...clients]
    .sort((a, b) => b.totPren - a.totPren)
    .slice(0, 10)
    .map((client) => ({
      ...client,
      totPrenotazioni: client.totPren,
    }));

  const appointments = {};
  let prenId = -2001;
  const days = [];
  for (let offset = -1; offset <= 10; offset += 1) {
    days.push(dayjs(today).add(offset, 'day'));
  }

  days.forEach((day) => {
    const key = day.format('YYYY-MM-DD');
    const weekday = day.day();
    const isSunday = weekday === 0;
    const list = [];
    chairs.forEach((chair, chairIndex) => {
      if (isSunday && chairIndex % 2 === 1) return;
      const bookings = isSunday ? 2 : 5 + Math.floor(rng() * 4);
      let hours = 9;
      let minutes = chairIndex % 2 === 0 ? 0 : 15;
      for (let i = 0; i < bookings; i += 1) {
        if (hours >= 18) break;
        if (hours === 13) {
          hours = 14;
          minutes = 0;
        }
        const client = clients[(chairIndex * 3 + i) % clients.length];
        const service = catalog.length ? catalog[(chairIndex + i) % catalog.length] : fallbackService;
        const start = padTime(hours, minutes);
        const endInfo = addMinutes(hours, minutes, service.durata);
        if (endInfo.hours >= 19) break;
        list.push({
          idprenotazione: prenId,
          idutente: client.idutente,
          nome: client.nome,
          cognome: client.cognome,
          cellulare: client.cellulare,
          email: client.email,
          orario_start: start,
          orario_end: endInfo.label,
          data_prenotazione: key,
          poltrona: workerId(chair),
          idpoltrona: workerId(chair),
          nota: '',
          stato: '0',
          status: 0,
          tipo: 0,
          visible: 1,
          disabled: 0,
          disabilitato: 0,
          self_prenotatione: 0,
          isBreak: 0,
          isFromGoogle: i === 4 ? 1 : 0,
          isFromPrivateApp: i === 1 ? 1 : 0,
          onlyApp: 0,
          servizi: [service],
        });
        prenId -= 1;
        hours = endInfo.hours;
        minutes = endInfo.minutes + 10;
        if (minutes >= 60) {
          hours += 1;
          minutes -= 60;
        }
      }
    });
    appointments[key] = list;
  });

  const todayAppointments = appointments[today] || [];
  const todayRevenue = todayAppointments.reduce(
    (sum, task) => sum + (Number(task.servizi?.[0]?.prezzo) || 25),
    0,
  );

  const queueByDay = days.slice(1, 5).map((day, dayIndex) => {
    const date = day.format('DD/MM/YYYY');
    const count = 3 + (dayIndex % 3);
    const list = [];
    for (let i = 0; i < count; i += 1) {
      const client = clients[(dayIndex * 4 + i + 6) % clients.length];
      const chair = chairs.length ? chairs[(dayIndex + i) % chairs.length] : null;
      list.push({
        nome: client.nome,
        cognome: client.cognome,
        cellulare: client.cellulare,
        tempo: 15 + i * 10,
        idUtente: client.idutente,
        idPoltrona: chair ? workerId(chair) : null,
        nomePoltrona: chair ? workerName(chair) : '',
        nome_referente: chair ? workerName(chair) : '',
        idListaAttesa: -3001 - dayIndex * 10 - i,
      });
    }
    return { date, list };
  });

  const reviews = clients.slice(0, 12).map((client, index) => {
    const rating = index === 8 ? 3.5 : index % 4 === 0 ? 4.5 : 5;
    return {
      id_recensione: -4001 - index,
      nome: client.nome,
      cognome: client.cognome,
      testo: REVIEW_TEXTS[index % REVIEW_TEXTS.length],
      rating_average: rating,
      rating_salone: rating,
      rating_servizio: Math.min(5, rating + 0.2),
      rating_personale: rating,
      data: dayjs(today).subtract(index + 1, 'day').toISOString(),
      segnalata: 0,
      hide: 0,
    };
  });

  const ratingDistribution = reviews.reduce(
    (acc, review) => {
      const stars = String(Math.round(review.rating_average));
      acc[stars] = (acc[stars] || 0) + 1;
      return acc;
    },
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  );

  const buildMonthExpenses = (monthKey, monthIndex) => {
    const rows = [];
    let id = -6001 - monthIndex * 40;
    chairs.forEach((chair, index) => {
      rows.push({
        idExpense: id,
        type: 2,
        date: `${monthKey}-01`,
        paid: monthIndex > 0 ? 1 : index % 2 === 0 ? 1 : 0,
        value: 980 + index * 70,
        idPoltrona: workerId(chair),
        bonusPct: index === 0 ? 8 : 5,
        valoreBonus: 80 + index * 10,
        isRecurring: 1,
        recurringEvery: 1,
        recurringEnd: dayjs(today).add(6, 'month').format('YYYY-MM'),
        note: '',
      });
      id -= 1;
    });
    rows.push(
      {
        idExpense: id - 1,
        type: 1,
        date: `${monthKey}-03`,
        paid: 1,
        value: 950,
        category: 1,
        note: 'Affitto locale',
        isRecurring: 1,
        recurringEvery: 1,
        recurringEnd: dayjs(today).add(6, 'month').format('YYYY-MM'),
      },
      {
        idExpense: id - 2,
        type: 1,
        date: `${monthKey}-08`,
        paid: monthIndex > 0 ? 1 : 0,
        value: 210,
        category: 2,
        note: 'Luce e gas',
        isRecurring: 1,
        recurringEvery: 1,
        recurringEnd: dayjs(today).add(6, 'month').format('YYYY-MM'),
      },
      {
        idExpense: id - 3,
        type: 1,
        date: `${monthKey}-18`,
        paid: 1,
        value: 95 + monthIndex * 20,
        category: 3,
        note: 'Prodotti per capelli',
        isRecurring: 0,
      },
    );
    return rows;
  };

  const expensesByMonth = {};
  for (let i = 0; i < 4; i += 1) {
    const monthKey = dayjs(today).subtract(i, 'month').format('YYYY-MM');
    expensesByMonth[monthKey] = buildMonthExpenses(monthKey, i);
  }

  const team = chairs.map((chair, index) => ({
    id: workerId(chair),
    name: workerName(chair),
    fatturato: 2200 + (chairs.length - index) * 380,
    prenotazioni: 28 + (chairs.length - index) * 4,
  }));

  const homeStat = {
    allClienti: clients.length,
    activeUser: clients.filter((client) =>
      dayjs(client.ultimo_accesso).isAfter(dayjs(today).subtract(30, 'day')),
    ).length,
    diffActiveUser: 12,
    allNewUserToday: 4,
    diffNewUserToday: 18,
    allPren: 412,
    allPrenThisWeek: todayAppointments.length + 22,
    diffPrenThisWeek: 8,
    allPrenToday: todayAppointments.length,
    diffPrentoday: 11,
    fatturatoOggi: todayRevenue,
    scontrinoMedioOggi: todayAppointments.length
      ? Math.round(todayRevenue / todayAppointments.length)
      : 28,
    utileOggi: Math.round(todayRevenue * 0.52),
    diffFatturatoOggi: 9,
    diffScontrinoMedioOggi: 3,
    diffUtileOggi: 7,
    bestUser,
  };

  return {
    clients,
    bestUser,
    appointments,
    queueByDay,
    reviews,
    ratingDistribution,
    expensesByMonth,
    team,
    homeStat,
    catalog,
    chairs,
  };
};

const periodScale = (payload = {}) => {
  const period = payload.period;
  if (period === 'oggi') return 1;
  if (period === 'settimana') return 6;
  if (period === 'mese') return 24;
  if (period === 'anno') return 280;
  if (payload.dataInizio && payload.dataFine) {
    const days = dayjs(payload.dataFine).diff(dayjs(payload.dataInizio), 'day') + 1;
    return Math.max(1, days);
  }
  return 24;
};

export const getDemoBundle = ({
  salonId,
  workers = [],
  services = [],
  today = dayjs().format('YYYY-MM-DD'),
}) => {
  const key = `${salonId}|${today}|${activeWorkers(workers)
    .map(workerId)
    .join(',')}|${activeServices(services).map(serviceId).join(',')}`;
  if (!cache.has(key)) {
    cache.set(key, buildBase({ salonId, workers, services, today }));
  }
  return cache.get(key);
};

export const getDemoAppointments = (bundle) => {
  const patches = getAppointmentPatches();
  const next = {};
  const push = (task) => {
    if (!task || isAppointmentDeleted(task.idprenotazione)) return;
    const patched = { ...task, ...(patches[Number(task.idprenotazione)] || {}) };
    const day = dayjs(patched.data_prenotazione).format('YYYY-MM-DD');
    if (!next[day]) next[day] = [];
    if (!next[day].some((item) => Number(item.idprenotazione) === Number(patched.idprenotazione))) {
      next[day].push(patched);
    }
  };
  Object.values(bundle.appointments || {}).forEach((list) => (list || []).forEach(push));
  getExtraAppointments().forEach(push);
  return next;
};

export const getDemoCalendarSlots = (bundle, { idPoltrona, nGiorniPrenotabili = 15 } = {}) => {
  const appointments = getDemoAppointments(bundle);
  const days = Math.max(1, Number(nGiorniPrenotabili) || 15);
  const result = [];
  for (let offset = 0; offset < days; offset += 1) {
    const day = dayjs().add(offset, 'day');
    const key = day.format('YYYY-MM-DD');
    const booked = (appointments[key] || []).filter(
      (task) =>
        String(task.idpoltrona ?? task.poltrona) === String(idPoltrona),
    );
    const orari = [];
    for (let minutes = 9 * 60; minutes < 19 * 60; minutes += 30) {
      if (minutes >= 13 * 60 && minutes < 14 * 60) continue;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const start = padTime(hours, mins);
      const endInfo = addMinutes(hours, mins, 30);
      const isBooked = booked.some(
        (task) => task.orario_start < endInfo.label && task.orario_end > start,
      );
      orari.push({
        oraInizio: start,
        oraFine: endInfo.label,
        isBooked: isBooked ? 1 : 0,
      });
    }
    result.push({ data: key, orari });
  }
  return result;
};

export const getDemoHomeQueue = (bundle) =>
  (bundle.queueByDay || []).map((day) => ({
    ...day,
    list: (day.list || []).filter((item) => !isQueueRemoved(item.idListaAttesa)),
  }));

export const getDemoCalendarQueue = (bundle, date) => {
  const day = dayjs(date || undefined);
  const entry =
    (bundle.queueByDay || []).find((item) =>
      dayjs(item.date, 'DD/MM/YYYY').isSame(day, 'day'),
    ) || bundle.queueByDay?.[0];
  const grouped = (bundle.chairs || []).map((chair) => ({
    idpoltrona: workerId(chair),
    nome_referente: workerName(chair),
    listaAttesa: [],
  }));
  (entry?.list || []).forEach((item) => {
    if (isQueueRemoved(item.idListaAttesa)) return;
    const row = grouped.find((chair) => String(chair.idpoltrona) === String(item.idPoltrona));
    if (row) row.listaAttesa.push(item);
    else if (grouped[0]) grouped[0].listaAttesa.push(item);
  });
  return grouped;
};

export const getDemoReviewsPayload = (bundle) => {
  const reviews = (bundle.reviews || []).map((review) =>
    isReviewReported(review.id_recensione) ? { ...review, segnalata: 1 } : review,
  );
  const avg =
    reviews.reduce((sum, review) => sum + Number(review.rating_average || 0), 0) /
    Math.max(1, reviews.length);
  return {
    reviews,
    averageRating: Number(avg.toFixed(1)),
    totalReviews: reviews.length,
    totalRatings: reviews.length,
    ratingDistribution: bundle.ratingDistribution,
  };
};

const CLIENT_FILTERS = {
  all: () => true,
  active: (client) =>
    Number(client.disabilitato) !== 1 &&
    dayjs(client.ultimo_accesso).isAfter(dayjs().subtract(30, 'day')),
  non_active: (client) =>
    Number(client.disabilitato) !== 1 &&
    !dayjs(client.ultimo_accesso).isAfter(dayjs().subtract(30, 'day')),
  disabled: (client) => Number(client.disabilitato) === 1,
  from_salon: (client) => Number(client.createFromSalone) === 1,
  new: (client) => Number(client.totPren) < 3,
};

export const getDemoClientsList = (
  bundle,
  { page = 1, limit = 20, search = '', filter = 'all' } = {},
) => {
  const extra = getExtraClients();
  let list = [...(bundle.clients || []), ...extra];
  const predicate = CLIENT_FILTERS[filter] || CLIENT_FILTERS.all;
  list = list.filter(predicate);
  if (search) {
    const q = String(search).trim().toLowerCase();
    list = list.filter((client) =>
      `${client.nome} ${client.cognome} ${client.email} ${client.cellulare}`
        .toLowerCase()
        .includes(q),
    );
  }
  const total = list.length;
  const start = Math.max(0, (Number(page) - 1) * Number(limit));
  const clienti = list.slice(start, start + Number(limit));
  const all = [...(bundle.clients || []), ...extra];
  return {
    clienti,
    pagination: {
      page: Number(page) || 1,
      limit: Number(limit) || 20,
      total,
      totalPages: Math.max(1, Math.ceil(total / Math.max(1, Number(limit) || 20))),
    },
    stats: {
      registeredClients: all.length,
      activeClients: all.filter(CLIENT_FILTERS.active).length,
      nonActiveClients: all.filter(CLIENT_FILTERS.non_active).length,
      disabledClients: all.filter(CLIENT_FILTERS.disabled).length,
      fromSalonClients: all.filter(CLIENT_FILTERS.from_salon).length,
      newClients: all.filter(CLIENT_FILTERS.new).length,
    },
  };
};

export const getDemoClientProfile = (bundle, idcliente) => {
  const extra = getExtraClients();
  const client = [...(bundle.clients || []), ...extra].find(
    (item) => Number(item.idutente) === Number(idcliente),
  );
  if (!client) return { cliente: { idutente: idcliente }, prenotazioni: [], notes: [] };
  const prenotazioni = Object.values(getDemoAppointments(bundle))
    .flat()
    .filter((task) => Number(task.idutente) === Number(idcliente))
    .map((task) => ({
      ...task,
      servizi: (task.servizi || []).map((service) => ({
        ...service,
        nomeServizio: service.nomeServizio || service.nome,
      })),
    }));
  return {
    cliente: client,
    prenotazioni,
    notes: [
      {
        idutentenota: -1,
        nota: 'Cliente demo: preferisce il taglio corto.',
        data: dayjs().subtract(4, 'day').toISOString(),
      },
    ],
    giornoPreferito: 'Sabato',
    ultimaPrenotazione: client.lastPren,
    servizioPreferito: bundle.catalog?.[0]?.nome || 'Taglio',
    poltronePrenotate: bundle.chairs?.[0] ? workerName(bundle.chairs[0]) : '',
    prenPerMese: Math.max(1, Math.round((client.totPren || 4) / 6)),
    orarioPreferito: [{ nomeFascia: 'Mattina' }],
  };
};

export const getDemoExpenses = (bundle, month) => {
  const base = bundle.expensesByMonth?.[month] || [];
  return [...base, ...getExtraExpenses(month)].filter((row) => !isExpenseDeleted(row.idExpense));
};

export const getDemoExpensesTrend = (bundle, months = 6) => {
  const rows = [];
  for (let i = months - 1; i >= 0; i -= 1) {
    const month = dayjs().subtract(i, 'month').format('YYYY-MM');
    const totale = getDemoExpenses(bundle, month).reduce(
      (sum, row) => sum + (Number(row.value) || 0),
      0,
    );
    rows.push({ month, totale });
  }
  return rows;
};

export const getDemoChairRevenue = (bundle, idPoltrona) => {
  const member = (bundle.team || []).find((item) => String(item.id) === String(idPoltrona));
  return { fatturato: member?.fatturato || 2400 };
};

export const getDemoHomeStat = (bundle) => bundle.homeStat;

export const getDemoFinance = (bundle, payload) => {
  const scale = periodScale(payload);
  const fatturato = Math.round(
    180 * scale + (bundle.homeStat?.fatturatoOggi || 0) * Math.min(scale, 8),
  );
  const uscite = Math.round(fatturato * 0.42);
  const prenotazioni = Math.round(8 * scale);
  const team = (bundle.team || []).map((member) => ({
    ...member,
    fatturato: Math.round((member.fatturato / 24) * scale),
    prenotazioni: Math.round((member.prenotazioni / 24) * scale),
  }));
  const current = {
    fatturato,
    entrate: fatturato,
    uscite,
    utile: fatturato - uscite,
    prenotazioni,
    scontrinoMedio: prenotazioni ? Math.round(fatturato / prenotazioni) : 28,
    team,
    finEntrateBreakdown: [
      ['Servizi', 78, 'c1', Math.round(prenotazioni * 0.78)],
      ['Prodotti', 22, 'c2', Math.round(prenotazioni * 0.22)],
    ],
    finUsciteBreakdown: [
      ['Staff', 55, 'c3', Math.round(uscite * 0.55)],
      ['Affitto', 25, 'c4', Math.round(uscite * 0.25)],
      ['Altro', 20, 'c5', Math.round(uscite * 0.2)],
    ],
  };
  return {
    ...current,
    prev: {
      fatturato: Math.round(fatturato * 0.91),
      entrate: Math.round(fatturato * 0.91),
      uscite: Math.round(uscite * 0.94),
      utile: Math.round((fatturato - uscite) * 0.88),
      prenotazioni: Math.round(prenotazioni * 0.9),
      scontrinoMedio: current.scontrinoMedio - 1,
      team: team.map((member) => ({
        ...member,
        fatturato: Math.round(member.fatturato * 0.9),
        prenotazioni: Math.round(member.prenotazioni * 0.9),
      })),
    },
  };
};

export const getDemoBookings = (bundle, payload) => {
  const scale = periodScale(payload);
  const prenotazioni = Math.round(8 * scale);
  const serviziItems = (bundle.catalog || []).slice(0, 5).map((service, index) => {
    const count = Math.max(2, Math.round(prenotazioni / Math.max(1, bundle.catalog.length) - index));
    return [service.nome, 0, `c${(index % 5) + 1}`, count];
  });
  const canali = [
    ['App', 0, 'appPrivata', Math.round(prenotazioni * 0.42)],
    ['Web', 0, 'web', Math.round(prenotazioni * 0.35)],
    ['Google', 0, 'google', Math.round(prenotazioni * 0.23)],
  ];
  return {
    canali,
    servizi: serviziItems,
    disdette: {
      totale: Math.round(prenotazioni * 0.08),
      daCliente: Math.round(prenotazioni * 0.05),
      daSalone: Math.round(prenotazioni * 0.03),
      prenotazioniTotale: prenotazioni,
    },
    ricorrenti: {
      totale: Math.round(prenotazioni * 0.18),
      monthsInPeriod: Math.max(1, Math.round(scale / 24) || 1),
      team: (bundle.team || []).map((member) => ({
        id: member.id,
        name: member.name,
        count: Math.round(member.prenotazioni * 0.18),
      })),
    },
    prev: {
      canali: canali.map((row) => [row[0], row[1], row[2], Math.round(row[3] * 0.9)]),
      servizi: serviziItems.map((row) => [row[0], row[1], row[2], Math.round(row[3] * 0.9)]),
      disdette: {
        totale: Math.round(prenotazioni * 0.07),
        daCliente: Math.round(prenotazioni * 0.04),
        daSalone: Math.round(prenotazioni * 0.03),
        prenotazioniTotale: Math.round(prenotazioni * 0.9),
      },
      ricorrenti: { totale: Math.round(prenotazioni * 0.16) },
    },
  };
};

export const getDemoQueueStats = (payload) => {
  const scale = periodScale(payload);
  const labels =
    scale <= 1
      ? ['09', '11', '13', '15', '17']
      : scale <= 7
        ? ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
        : ['Sett 1', 'Sett 2', 'Sett 3', 'Sett 4'];
  const queue = labels.map((label, index) => ({
    label,
    queue: 4 + ((index * 3) % 7),
    recovered: 2 + (index % 4),
  }));
  const totalQueue = queue.reduce((sum, row) => sum + row.queue, 0);
  const totalRecovered = queue.reduce((sum, row) => sum + row.recovered, 0);
  return {
    queue,
    queueSummary: {
      items: [
        ['In coda', totalQueue, 'queue'],
        ['Recuperati', totalRecovered, 'recovered'],
      ],
      totalQueue,
      totalRecovered,
    },
    prev: {
      queueSummary: {
        totalQueue: Math.round(totalQueue * 0.86),
        totalRecovered: Math.round(totalRecovered * 0.8),
      },
    },
  };
};

export const getDemoClientsStats = (bundle) => {
  const giorni = [
    { label: 'Lun', count: 18 },
    { label: 'Mar', count: 16 },
    { label: 'Mer', count: 21 },
    { label: 'Gio', count: 19 },
    { label: 'Ven', count: 24 },
    { label: 'Sab', count: 31 },
    { label: 'Dom', count: 8 },
  ];
  return {
    clientiAttivi: bundle.homeStat.activeUser,
    clientiTotali: bundle.clients.length,
    prenotazioni: bundle.homeStat.allPrenThisWeek,
    fatturato: Math.round((bundle.homeStat.fatturatoOggi || 400) * 18),
    clientiRischioAbbandono: 6,
    clientiPericolosiCount: 2,
    giorni,
    byPoltrona: (bundle.team || []).map((member, index) => ({
      id: member.id,
      clientiAttivi: Math.max(4, bundle.homeStat.activeUser - index * 3),
      clientiTotali: Math.max(6, bundle.clients.length - index * 2),
      prenotazioni: member.prenotazioni,
      fatturato: member.fatturato,
      clientiRischioAbbandono: 1 + (index % 2),
      clientiPericolosiCount: index === 0 ? 1 : 0,
      giorni: giorni.map((day) => ({ ...day, count: Math.max(1, day.count - index * 2) })),
    })),
  };
};

export const getDemoBadClients = (bundle) =>
  (bundle.clients || []).slice(18, 20).map((client) => ({
    utente: client.idutente,
    idutente: client.idutente,
  }));

export const findDemoClient = (bundle, idutente) => {
  const extra = getExtraClients();
  return [...(bundle.clients || []), ...extra].find(
    (item) => Number(item.idutente) === Number(idutente),
  );
};

export const buildLocalAppointment = (payload, client) => {
  const task = {
    idprenotazione: nextDemoId(),
    idutente: payload.idUtente || client?.idutente || null,
    nome: client?.nome || payload.nota || 'Cliente',
    cognome: client?.cognome || '',
    cellulare: client?.cellulare || '',
    orario_start: payload.oraInizio,
    orario_end: payload.oraFine,
    data_prenotazione: payload.dataPrenotazione,
    poltrona: payload.idPoltrona,
    idpoltrona: payload.idPoltrona,
    nota: payload.nota || '',
    stato: '0',
    status: 0,
    servizi: payload.servizi || [],
    self_prenotatione: 1,
    isBreak: 0,
    disabled: 0,
    disabilitato: 0,
  };
  addDemoAppointment(task);
  return task;
};
